/**
 * Spend on other accounts, expanded (ADR 0024 §3): every account this person
 * can open where the fan has spent, the open one included, with lifetime,
 * 30-day and last purchase. A row opens the fan's chat on that account.
 */
import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { crmFetch, dataOf } from '../chat/api'
import { money } from '../chat/format'
import { FeatherIcon } from '../components/FeatherIcon'
import { Spinner } from '../components/Spinner'
import type { CrmAccount } from '../crm/crmAccess'
import { Avatar } from '../workspace/widgets/Avatar'
import type { OpenChat } from './sideBlocks'
import '../workspace/workspace.css'

export interface SpendRow {
  account_id: string
  lifetime: number
  last_30d: number
  last_purchase_at: string | null
  last_purchase_amount: number | null
}

type Sort = 'lifetime' | 'last_30d' | 'last_purchase_at'
const SORTS: Sort[] = ['lifetime', 'last_30d', 'last_purchase_at']

const time = (iso: string | null) => (iso ? Date.parse(iso) || 0 : 0)

function sortValue(r: SpendRow, sort: Sort): number {
  return sort === 'last_purchase_at' ? time(r.last_purchase_at) : r[sort]
}

interface Props {
  accountId: string
  fanId: string
  fanName: string
  accounts: CrmAccount[]
  onOpenChat?: OpenChat
  onClose: () => void
}

export function SpendAcrossOverlay({ accountId, fanId, fanName, accounts, onOpenChat, onClose }: Props) {
  const { t, i18n } = useTranslation('chat')
  const [state, setState] = useState<{ total: number; total30: number; rows: SpendRow[] } | 'loading' | 'error'>('loading')
  const [sort, setSort] = useState<Sort>('lifetime')
  const [query, setQuery] = useState('')

  useEffect(() => {
    let alive = true
    // No exclusion: the open account is listed too, marked.
    crmFetch('/v1/fans/spend-across', { accountId, local: true, body: { fan_id: fanId } })
      .then((body) => {
        const d = (dataOf(body) || {}) as { total_lifetime?: number; total_last_30d?: number; accounts?: SpendRow[] }
        if (alive) setState({ total: Number(d.total_lifetime) || 0, total30: Number(d.total_last_30d) || 0, rows: Array.isArray(d.accounts) ? d.accounts : [] })
      })
      .catch(() => alive && setState('error'))
    return () => {
      alive = false
    }
  }, [accountId, fanId])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const byId = useMemo(() => new Map(accounts.map((a) => [a.account_id, a])), [accounts])
  const nameOf = (id: string) => {
    const a = byId.get(id)
    return a?.profile_name || a?.username || id
  }
  const rows = useMemo(() => {
    if (typeof state !== 'object') return []
    const q = query.trim().toLowerCase()
    const list = q
      ? state.rows.filter((r) => {
          const a = byId.get(r.account_id)
          return [a?.profile_name, a?.username, r.account_id].some((v) => String(v || '').toLowerCase().includes(q))
        })
      : state.rows
    return [...list].sort((a, b) => sortValue(b, sort) - sortValue(a, sort))
  }, [state, query, sort, byId])
  const date = (iso: string | null) => {
    const d = iso ? new Date(iso) : null
    return d && Number.isFinite(d.getTime()) ? d.toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' }) : '—'
  }

  return createPortal(
    <div className="rc-chat rc-overlay-root">
      <div className="cp-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
        <div className="rc-spend-overlay" role="dialog" aria-modal="true" aria-label={t('side.spendAcross.overlay', { name: fanName })}>
          <div className="rc-spend-head">
            <FeatherIcon name="layers" size={16} />
            <div className="rc-spend-title">
              <b>{t('side.spendAcross.overlayTitle')}</b>
              <span className="rc-muted">{fanName}</span>
            </div>
            <button type="button" className="rc-icon-btn" aria-label={t('common.close')} title={t('common.close')} onClick={onClose}>
              <FeatherIcon name="x" size={15} />
            </button>
          </div>
          {typeof state === 'object' && (
            <div className="rc-spend-totals">
              <div>
                <span>{t('side.spendAcross.lifetime')}</span>
                <b>{money(state.total)}</b>
              </div>
              <div>
                <span>{t('side.spendAcross.days30')}</span>
                <b>{money(state.total30)}</b>
              </div>
              <div>
                <span>{t('side.spendAcross.accounts')}</span>
                <b>{state.rows.length}</b>
              </div>
            </div>
          )}
          {typeof state === 'object' && state.rows.length > 0 && (
            <div className="rc-spend-tools">
              <label className="rc-spend-search">
                <FeatherIcon name="search" size={13} />
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('side.spendAcross.find')} aria-label={t('side.spendAcross.find')} />
              </label>
              <div className="ws-seg rc-side-seg" role="tablist" aria-label={t('side.spendAcross.sortBy')}>
                {SORTS.map((s) => (
                  <button key={s} type="button" role="tab" aria-selected={sort === s} className={sort === s ? 'is-on' : ''} onClick={() => setSort(s)}>
                    {t(`side.spendAcross.sort.${s}`)}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="rc-spend-body rc-fan-scroll">
            {state === 'loading' ? (
              <div className="rc-spend-empty">
                <Spinner size={16} />
              </div>
            ) : state === 'error' ? (
              <div className="rc-spend-empty rc-error">{t('side.spendAcross.error')}</div>
            ) : !state.rows.length ? (
              <div className="rc-spend-empty rc-muted">{t('side.spendAcross.noneAnywhere')}</div>
            ) : !rows.length ? (
              <div className="rc-spend-empty rc-muted">{t('side.spendAcross.noMatch')}</div>
            ) : (
              <ul className="rc-spend-list" aria-label={t('side.spendAcross.overlayTitle')}>
                {rows.map((r) => {
                  const here = r.account_id === accountId
                  const a = byId.get(r.account_id)
                  return (
                    <li key={r.account_id}>
                      <button
                        type="button"
                        className={`rc-spend-row ${here ? 'is-here' : ''}`}
                        aria-label={nameOf(r.account_id)}
                        title={here ? t('side.spendAcross.here') : t('side.openOn', { account: nameOf(r.account_id) })}
                        onClick={() => {
                          if (!here) onOpenChat?.(r.account_id, fanId, fanName)
                          onClose()
                        }}
                      >
                        <Avatar src={a?.avatar_url || null} name={nameOf(r.account_id)} size={30} />
                        <span className="rc-spend-acc">
                          <b>
                            {nameOf(r.account_id)}
                            {here && <span className="rc-spend-here">{t('side.spendAcross.here')}</span>}
                          </b>
                          <span className="rc-muted">
                            {a?.username ? `@${a.username}` : ''}
                            {r.last_purchase_at && ` · ${t('side.spendAcross.lastOn', { amount: money(r.last_purchase_amount), date: date(r.last_purchase_at) })}`}
                          </span>
                        </span>
                        <span className="rc-spend-num">
                          <b>{money(r.lifetime)}</b>
                          <span className="rc-muted">{t('side.spendAcross.last30', { amount: money(r.last_30d) })}</span>
                        </span>
                        <FeatherIcon name={here ? 'check' : 'arrow-up-right'} size={14} className="rc-spend-go" />
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
          <p className="rc-spend-foot">{t('side.spendAcross.foot')}</p>
        </div>
      </div>
    </div>,
    document.body,
  )
}
